import { create } from 'zustand'
import type { ChatMessage, OnlineUser } from '../types'

interface ChatState {
  messages: ChatMessage[]
  onlineUsers: OnlineUser[]
  isLoading: boolean
  hasMore: boolean
  unreadCount: number
  
  // Actions
  setMessages: (messages: ChatMessage[]) => void
  addMessage: (message: ChatMessage) => void
  updateMessage: (messageId: string, updates: Partial<ChatMessage>) => void
  removeMessage: (messageId: string) => void
  prependMessages: (messages: ChatMessage[]) => void
  setOnlineUsers: (users: OnlineUser[]) => void
  updateUserTyping: (userId: string, isTyping: boolean) => void
  setLoading: (isLoading: boolean) => void
  setHasMore: (hasMore: boolean) => void
  setUnreadCount: (count: number) => void
  incrementUnreadCount: () => void
  resetUnreadCount: () => void
  clearMessages: () => void
}

export const useChatStore = create<ChatState>((set) => ({
  messages: [],
  onlineUsers: [],
  isLoading: false,
  hasMore: true,
  unreadCount: 0,

  setMessages: (messages) => 
    set({ messages }),

  addMessage: (message) =>
    set((state) => {
      // Evita duplicatas
      const exists = state.messages.some((m) => m.id === message.id)
      if (exists) return state

      return {
        messages: [...state.messages, message],
      }
    }),

  updateMessage: (messageId, updates) =>
    set((state) => ({
      messages: state.messages.map((msg) =>
        msg.id === messageId ? { ...msg, ...updates } : msg
      ),
    })),

  removeMessage: (messageId) =>
    set((state) => ({
      messages: state.messages.filter((msg) => msg.id !== messageId),
    })),

  prependMessages: (messages) =>
    set((state) => {
      // Filtra mensagens duplicadas
      const existingIds = new Set(state.messages.map((m) => m.id))
      const newMessages = messages.filter((m) => !existingIds.has(m.id))
      
      return {
        messages: [...newMessages, ...state.messages],
      }
    }),

  setOnlineUsers: (users) =>
    set({ onlineUsers: users }),

  updateUserTyping: (userId, isTyping) =>
    set((state) => ({
      onlineUsers: state.onlineUsers.map((user) =>
        user.user_id === userId ? { ...user, is_typing: isTyping } : user
      ),
    })),

  setLoading: (isLoading) =>
    set({ isLoading }),

  setHasMore: (hasMore) =>
    set({ hasMore }),

  setUnreadCount: (count) =>
    set({ unreadCount: count }),

  incrementUnreadCount: () =>
    set((state) => ({ unreadCount: state.unreadCount + 1 })),

  resetUnreadCount: () =>
    set({ unreadCount: 0 }),

  clearMessages: () =>
    set({ messages: [], hasMore: true }),
}))

